import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { allProducts, productCategories } from '../../mocks/products';
import { useCart } from '../../contexts/CartContext';
import { showToast } from '../../components/feature/Toast';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function ShopPage() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { addItem } = useCart();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Research Peptides Shop | Laboratory-Grade Biotech Compounds',
    description: 'Browse our complete catalog of research-grade peptides and biotech compounds. High-purity products with COA documentation, third-party testing, and quote-based ordering for laboratories.',
    keywords: 'research peptides shop, buy research peptides, laboratory peptides, biotech compounds catalog, peptide products, research chemicals',
    canonical: `${siteUrl}/shop`,
    schema: generateWebPageSchema(siteUrl, '/shop', 'Research Peptides Shop | Laboratory-Grade Biotech Compounds', 'Browse our complete catalog of research-grade peptides and biotech compounds. High-purity products with COA documentation, third-party testing, and quote-based ordering for laboratories.')
  });

  const filteredProducts = useMemo(() => {
    let filtered = allProducts;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.specification.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [selectedCategory, searchQuery]);

  const handleAddToCart = (product: typeof allProducts[0]) => {
    try {
      addItem({
        id: product.id,
        productName: product.name,
        dosage: product.specification,
        price: product.price,
        image: product.image,
        slug: product.slug,
      });
      showToast(`${product.name} added to cart`, 'success');
    } catch (error) {
      console.error('Add to cart failed:', error);
      showToast('Failed to add product to cart', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <Navbar />

      {/* Header */}
      <section className="pt-32 pb-16 bg-gradient-biotech">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 flex items-center justify-center bg-primary-400/20 rounded-lg backdrop-blur-sm">
              <i className="ri-flask-line text-xl text-primary-400"></i>
            </div>
            <span className="text-white/80 text-sm font-medium">DNBio Research Catalog</span>
          </div>
          <h1 className="text-white mb-4">
            Research Product Catalog
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mb-6">
            Browse our complete selection of high-purity research peptides and biotech compounds with third-party verification.
          </p>
          
          {/* Research Use Only Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-400/30 rounded-lg backdrop-blur-sm">
            <i className="ri-alert-line text-red-300 text-lg"></i>
            <span className="text-red-100 text-sm font-semibold">For Research Use Only – Not for Human Consumption</span>
          </div>
        </div>
      </section>

      {/* Filters & Search */}
      <section className="sticky top-20 z-40 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="container-custom py-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="w-full lg:w-96">
              <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input pl-12 text-sm"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`btn btn-sm ${
                  selectedCategory === 'all'
                    ? 'btn-primary'
                    : 'btn-ghost border border-gray-300'
                }`}
              >
                All Products
              </button>
              {productCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`btn btn-sm ${
                    selectedCategory === cat.id
                      ? 'btn-primary'
                      : 'btn-ghost border border-gray-300'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="section-sm">
        <div className="container-custom">
          <div className="mb-8">
            <p className="text-navy-600 font-medium">
              Showing <strong className="text-navy-900">{filteredProducts.length}</strong> {filteredProducts.length === 1 ? 'product' : 'products'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-product-shop>
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="card card-hover group"
              >
                <div className="relative w-full aspect-square overflow-hidden bg-gray-50 p-8 rounded-t-xl">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  {/* Research Use Only Badge */}
                  <div className="absolute top-3 right-3">
                    <span className="badge badge-error text-xs">
                      Research Only
                    </span>
                  </div>
                </div>
                <div className="card-body">
                  <h3 className="text-lg font-bold text-navy-900 mb-2">
                    {product.name}
                  </h3>
                  <p className="text-sm text-navy-600 mb-3">
                    {product.specification}
                  </p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="badge badge-success">
                      {product.purity} Pure
                    </span>
                    <span className="text-xl font-bold text-navy-900">
                      ${product.price.toFixed(2)}
                    </span>
                  </div>
                  <div className="space-y-2.5">
                    <button
                      onClick={() => handleAddToCart(product)}
                      className="btn btn-primary w-full btn-sm whitespace-nowrap"
                    >
                      <i className="ri-shopping-cart-line text-base"></i>
                      Add to Cart
                    </button>
                    <Link
                      to={`/product/${product.slug}`}
                      className="btn btn-outline w-full btn-sm whitespace-nowrap"
                    >
                      Request Quote
                    </Link>
                    <Link
                      to={`/product/${product.slug}`}
                      className="btn btn-ghost w-full btn-sm text-navy-600 hover:text-navy-900 whitespace-nowrap"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-20">
              <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-6">
                <i className="ri-search-line text-4xl text-gray-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-navy-900 mb-2">No products found</h3>
              <p className="text-navy-600 mb-6">Try adjusting your filters or search query</p>
              <button
                onClick={() => {
                  setSelectedCategory('all');
                  setSearchQuery('');
                }}
                className="btn btn-primary whitespace-nowrap"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
